<template>
  <div class="fixed inset-0 z-50 overflow-hidden">
    <div class="absolute inset-0 bg-black bg-opacity-50" @click="$emit('close')"></div>

    <div class="absolute inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-2xl shadow-xl max-h-[90vh] flex flex-col">
      <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Select Customer</h2>
        <button
          @click="$emit('close')"
          class="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="p-4 border-b border-gray-200 dark:border-gray-700">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search customers..."
            class="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <svg class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-4">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400">Loading customers...</p>
        </div>

        <div v-else-if="filteredCustomers.length === 0" class="text-center py-8">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p class="mt-4 text-gray-500 dark:text-gray-400">No customers found</p>
        </div>

        <div v-else class="space-y-2">
          <button
            v-for="customer in filteredCustomers"
            :key="customer.id ?? 1"
            @click="selectCustomer(customer)"
            class="w-full p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg text-left transition-colors"
          >
            <div class="flex items-start space-x-3">
              <div class="flex-1 min-w-0">
                <p class="text-base font-semibold text-gray-900 dark:text-white">{{ customer.name }}</p>
                <div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6.5 2h11a2 2 0 012 2v16a2 2 0 01-2 2h-11a2 2 0 01-2-2V4a2 2 0 012-2zM12 18h.01"></path>
                  </svg>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ customer.mobile }}</p>
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    <span
                    v-if="Number(customer.credit_limit) - Number(customer.outstanding) < 0"
                    class="inline-block px-2 py-1 rounded-full bg-red-100 text-red-700"
                  >
                    Credit Balance: {{ formatNumber(Number(customer.credit_limit) - Number(customer.outstanding)) }}
                  </span>
                  <span
                    v-else
                    class="inline-block px-2 py-1 rounded-full bg-blue-100 text-blue-700"
                  >
                    Credit Balance: {{ formatNumber(Number(customer.credit_limit) - Number(customer.outstanding)) }}
                  </span>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <div class="p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          @click="createNewCustomer"
          class="w-full px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Create New Customer</span>
        </button>
      </div>
    </div>
    <MobileCustomerCreateModal v-if="showCustomerCreateForm" @close="showCustomerCreateForm =false"
    @save="handleCustomerCreated"
    :customerList="customers" 
    :customerTypeOptions="customerTypes"
    :customerGroupOptions="customerGroups"
    :countryOptions="countrys" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import MobileCustomerCreateModal from './MobileCustomerCreateModal.vue'
import { Customer } from '../../../../../types/models'
import { formatNumber } from '../../../../../utils/CommonFunctions'
import { posStore } from '../../../../../stores/posStore'

const pos = posStore();

const emit = defineEmits(['close', 'select', 'load-customers'])
const props = defineProps<{
  customerGroups?: any[]
  customerTypes?: any[]
  countrys?: any[]
  customers?: any[]
}>();

const searchQuery = ref('')
const filteredCustomers = ref<Customer[]>([])
const loading = ref(false)

watch(() => searchQuery.value, (newVal) => {
  if (newVal.trim() === '') {
    filteredCustomers.value = props.customers || []
  } else {
    const query = newVal.toLowerCase()
    filteredCustomers.value = (props.customers || []).filter(customer =>
      customer.name.toLowerCase().includes(query) ||
      (customer.mobile && customer.mobile.toLowerCase().includes(query))
    )
  }  
})


const handleCustomerCreated = (newCustomer: Customer) => {
  // Add the new customer to the list
  pos.setCustomer(newCustomer);
  emit('load-customers')
  emit('close')
}

const selectCustomer = (customer: Customer) => {
  pos.setCustomer(customer);
  emit('close')
}

const showCustomerCreateForm = ref(false)

const createNewCustomer = () => {
  showCustomerCreateForm.value = true
}

onMounted(() => {
  filteredCustomers.value = props.customers || []
  //loadCustomers()
})
</script>
